import React from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Check } from "lucide-react";

const IllustrationBeverage = () => (
  <svg viewBox="0 0 400 280" className="w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <defs>
      <pattern id="grid-bev" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="400" height="280" fill="#1d428a" />
    <rect width="400" height="280" fill="url(#grid-bev)" />
    {/* Conveyor belt */}
    <rect x="0" y="200" width="400" height="22" fill="#0f2a5c" />
    <rect x="0" y="222" width="400" height="6" fill="#df4661" />
    {[...Array(20)].map((_, i) => (
      <rect key={i} x={i * 22 + 4} y="206" width="14" height="10" fill="#1d428a" stroke="#fafbfd" strokeWidth="0.6" />
    ))}
    {/* Bottles */}
    {[60, 130, 200, 270, 340].map((cx, i) => (
      <g key={cx} transform={`translate(${cx}, 110)`}>
        <rect x="-12" y="0" width="24" height="6" fill="#fafbfd" />
        <rect x="-9" y="6" width="18" height="10" fill="#df4661" />
        <rect x="-14" y="16" width="28" height="78" rx="4" fill="#fafbfd" opacity="0.92" />
        <rect x="-14" y="34" width="28" height="22" fill="#df4661" />
        <text x="0" y="49" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="6" fill="#fafbfd">CH</text>
      </g>
    ))}
    {/* Overhead frame */}
    <rect x="20" y="40" width="360" height="6" fill="#fafbfd" opacity="0.4" />
    <rect x="20" y="40" width="6" height="60" fill="#fafbfd" opacity="0.4" />
    <rect x="374" y="40" width="6" height="60" fill="#fafbfd" opacity="0.4" />
    {/* Labels */}
    <text x="20" y="265" fontFamily="JetBrains Mono" fontSize="8" fill="#fafbfd" opacity="0" letterSpacing="2">.</text>
  </svg>
);

const IllustrationMachineTool = () => (
  <svg viewBox="0 0 400 280" className="w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <defs>
      <pattern id="grid-mt" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(29,66,138,0.08)" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="400" height="280" fill="#e8ecf3" />
    <rect width="400" height="280" fill="url(#grid-mt)" />
    {/* Bed of machine */}
    <rect x="40" y="190" width="320" height="40" fill="#1d428a" />
    <rect x="40" y="230" width="320" height="6" fill="#df4661" />
    {/* Tool head */}
    <rect x="170" y="60" width="60" height="100" fill="#1d428a" />
    <rect x="160" y="50" width="80" height="14" fill="#0f2a5c" />
    <rect x="190" y="160" width="20" height="40" fill="#fafbfd" stroke="#1d428a" strokeWidth="1.5" />
    {/* Spindle gear */}
    <g transform="translate(200, 120)">
      <circle r="22" fill="#df4661" />
      {[...Array(10)].map((_, i) => {
        const a = (i * 36 * Math.PI) / 180;
        const x = Math.cos(a) * 28;
        const y = Math.sin(a) * 28;
        return <rect key={i} x={x - 4} y={y - 4} width="8" height="8" fill="#df4661" transform={`rotate(${i * 36} ${x} ${y})`} />;
      })}
      <circle r="8" fill="#1d428a" />
    </g>
    {/* Workpiece */}
    <rect x="80" y="170" width="60" height="22" fill="#5A6270" />
    <rect x="260" y="170" width="60" height="22" fill="#5A6270" />
    {/* Coordinate axis */}
    <g transform="translate(345, 80)">
      <line x1="0" y1="0" x2="30" y2="0" stroke="#1d428a" strokeWidth="1.5" />
      <line x1="0" y1="0" x2="0" y2="30" stroke="#1d428a" strokeWidth="1.5" />
      <text x="32" y="3" fontFamily="JetBrains Mono" fontSize="8" fill="#1d428a">X</text>
      <text x="-3" y="38" fontFamily="JetBrains Mono" fontSize="8" fill="#1d428a">Z</text>
    </g>
    {/* Measurement marks */}
    {[60, 140, 220, 300].map((x) => (
      <g key={x}>
        <line x1={x} y1="240" x2={x} y2="248" stroke="#1d428a" strokeWidth="1" />
        <text x={x} y="262" fontFamily="JetBrains Mono" fontSize="7" fill="#5A6270" textAnchor="middle">{x}mm</text>
      </g>
    ))}
    <text x="40" y="265" fontFamily="JetBrains Mono" fontSize="8" fill="#1d428a" opacity="0" letterSpacing="2">.</text>
  </svg>
);

const ILLUSTRATIONS = [IllustrationBeverage, IllustrationMachineTool];

export default function Industries() {
  const { t } = useLanguage();

  return (
    <section id="industries" data-testid="industries-section" className="py-20 md:py-24 relative bg-white">
      <div className="absolute inset-0 tech-grid-fine opacity-40 pointer-events-none" />
      <div className="relative px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-y-8 mb-10">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono-tech text-[#1d428a]">
              <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
              {t.industries.eyebrow}
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 data-testid="industries-title" className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A]">
              {t.industries.title}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1d428a]/15 border border-[#1d428a]/15">
          {t.industries.cards.map((c, i) => {
            const Illustration = ILLUSTRATIONS[i % ILLUSTRATIONS.length];
            return (
              <article
                key={i}
                data-testid={`industry-card-${i}`}
                className="group bg-white"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <div className="absolute inset-0 transition-transform duration-[1400ms] group-hover:scale-105">
                    <Illustration />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
                  <div className="absolute top-4 left-4 font-mono-tech text-white bg-[#df4661] px-2.5 py-1 z-10">
                    {c.tag}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 z-10">
                    <div className="font-display text-3xl md:text-4xl text-white drop-shadow">
                      {c.name}
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <p className="text-[#5A6270] text-sm leading-relaxed">{c.desc}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[#0A0A0A]">
                        <Check size={16} strokeWidth={2} className="text-[#df4661] mt-0.5 shrink-0" />
                        <span className="text-sm">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
